// Közös adat-tábla — oszlop-config, szerver-rendezés, végtelen görgetés (IntersectionObserver),
// sor-akció-slot, üres/loading(skeleton)/error állapot, keskeny nézeten kártyás fallback.
//
// LAYOUT (lg+): rögzített, lekerekített kártya (overflow-hidden) → a fejléc a függőleges görgetőn KÍVÜL, statikusan
// ül; alatta a sorok saját görgetőben → a görgetősáv az ELSŐ SORTÓL indul (nem fut a fejléc mellett). A fejléc és a
// sorok EGY közös CSS-grid-sablonon osztoznak (tökéletes igazítás, `<table>` nélkül; a11y: role="table"/row/cell).
// Az oszlopok alapból `minmax(0,1fr)` (rugalmas + tartalom-vágó) → SOHA nincs vízszintes kilógás/szürke sáv.
//
// Ez a KÖZÖS lista-primitív (a Clients a referencia-fogyasztó; a többi oldal migrációja külön kör).
// A rendezést/lapozást a hívó vezérli (server-driven): a fejléc-kattintás `onSort(col)`-t hív, a
// görgetés-sentinel láthatóvá válásakor `onLoadMore()` fut (ha van `hasNextPage` és nincs folyamatban).
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import {
  ArrowDownIcon,
  ArrowsUpDownIcon,
  ArrowUpIcon,
  EllipsisHorizontalIcon,
} from '@heroicons/react/24/outline';
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CardDetailList,
  CARD_DETAIL_SCALE,
  type CardDetailItem,
} from '@/components/shared/card-detail-list';
import { InCardContext } from '@/components/shared/details-expander';
import { EmptyState } from '@/components/shared/empty-state';
import { RowActionsVariantContext } from '@/components/shared/list-row-actions';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { Text } from '@/components/ui/text';
import { HAIRLINE_BOTTOM, HAIRLINE_ROW } from '@/components/shared/hairline';
import { cn } from '@/lib/utils';

export type SortOrder = 'asc' | 'desc';

/** A KESKENY (<lg) kártya-nézet slotjai (a desktop-tábla oszlop-sorrendjét NEM érinti). Best practice: az
 *  ÖSSZECSUKOTT kártya EGYSÉGES méretű — csak a `title` + `status` látszik; MINDEN MÁS a „Részletek" gomb alatt,
 *  helyben kibontva (label:érték lista) jelenik meg. Így minden kártya egyforma, a lista jól pásztázható.
 *  - `title`     — a kártya elsődleges (félkövér) sora, balra, `flex-1 truncate` (mindig látszik).
 *  - `status`    — badge a title-sor JOBB oldalán (`shrink-0`), a ⋯-menü mellett (mindig látszik).
 *  - `subtitle` / `meta` / `trailing` — visszafelé-kompat. aliasok: MIND a „Részletek" (kibontott) blokkba kerül,
 *    a többi (slot nélküli) oszloppal együtt. Külön collapsed-megjelenítésük megszűnt (egységes kártya).
 *  - `hidden`    — a kártyán SEHOL nem jelenik meg (a `hideOnCard: true` ekvivalense; csak a detail/sheet-ben). */
export type CardSlot = 'title' | 'subtitle' | 'meta' | 'status' | 'trailing' | 'hidden';

export interface DataTableColumn<T> {
  /** Egyedi oszlop-azonosító (React-kulcs + rendezés-alap, ha nincs külön sortKey). */
  id: string;
  header: ReactNode;
  /** A cella-tartalom egy sorból. */
  cell: (row: T) => ReactNode;
  /** Rendezhető-e (fejléc-kattintás). A rendezés-kulcs alapból az `id` (vagy a `sortKey`). */
  sortable?: boolean;
  sortKey?: string;
  /** Cella-osztály (pl. text-muted-foreground, igazítás). */
  className?: string;
  headerClassName?: string;
  /** A keskeny (kártyás) nézeten a mező felirata. Best practice szerint a slot-kártya MÁR NEM renderel látható
   *  „Címke:" feliratot — ez legfeljebb `aria-label`-nek marad meg (visszafelé-kompatibilitás). */
  cardLabel?: ReactNode;
  /** A KESKENY (<lg) slot-kártyán betöltött szerep (lásd `CardSlot`). Ha hiányzik: az ELSŐ oszlop `title`,
   *  az `id==='status'` oszlop `status`, MINDEN MÁS `hidden` (tiszta kártya: csak cím + státusz — NEM a régi
   *  label:érték dump). A `hideOnCard: true` ekvivalens a `card: 'hidden'`-nel (előbbi megmarad kompatibilisan). */
  card?: CardSlot;
  /** Az oszlop grid-track mérete (lg+ táblanézet). Alap: `minmax(0,1fr)` (rugalmas, tartalom-vágással —
   *  így SOHA nincs vízszintes kilógás). Használható pl. `minmax(0,2fr)`, `120px`. Kerüld a `max-content`/`auto`-t
   *  (tartalom-függő → a fejléc és a sorok külön rácsa eltérően oldódna fel → elcsúszó oszlopok). */
  width?: string;
  /** Többsoros cella (pl. név+badge, cím-blokk, lista): NEM kap `truncate`-et (egysoros vágást), csak clip.
   *  A cellán belüli egyes szövegek maguk truncate-elhetnek, ha kell. */
  multiline?: boolean;
  /** A KESKENY (kártyás, <lg) nézeten REJTVE — a túlzsúfolt mobil-kártya karcsúsítására (best practice: a
   *  kártya-fejléc [első oszlop] + 1-2 lényegi metaadat + státusz maradjon; a kevésbé fontos oszlopok elrejtve).
   *  A desktop-tábla (lg+) VÁLTOZATLAN — ez KIZÁRÓLAG a mobil-kártya meta-listát szűri. Az ELSŐ oszlop (kártya-
   *  fejléc) sosem rejthető. Alap: `false` (a kártya minden oszlopot mutat — a meglévő oldalak viselkedése). */
  hideOnCard?: boolean;
  /**
   * KÁRTYA-MÓD (mobil, <lg): a cella HELYETTE ezzel renderel. Akkor kell, ha a desktop-cella SAJÁT
   * kibontót (`aria-expanded` gomb) tartalmaz.
   *
   * A LELET, ami miatt létezik (user): a kártyán KÉT „Részletek" gomb állt egymás mellett — a
   * cella sajátja és a kártya CC4-es generikusa. Ok: az összecsukott kártya-fejlécnek EGYSÉGES magasságúnak
   * kell lennie (CC4), a cella belső kibontója viszont megnyitható tartalmat tett bele. Mérve pontosan két
   * lista-oldalon fordult elő: `activity-log.tsx` és `assets.tsx`.
   *
   * A helyes alak: a `cardCell` a fejlécbe CSAK a címet adja, a kibontó tartalma pedig a `cardExtra`-val a
   * kártya „Részletek" blokkjába kerül — így a kártyán PONTOSAN EGY kibontó van. A desktop-út változatlan.
   */
  cardCell?: ((row: T) => ReactNode) | undefined;
  /**
   * KÁRTYA-MÓD: extra tartalom a „Részletek" blokk aljára, TELJES szélességben (a label:érték lista után).
   * Ide kerül az, amit a desktop-cella a saját kibontójában mutat — kibontó NÉLKÜL, mert a kártya
   * „Részletek" gombja már a kapcsoló. Lásd a `cardCell` magyarázatát.
   */
  cardExtra?: ((row: T) => ReactNode) | undefined;
  /**
   * KÁRTYA-MÓD, `label: érték` párok: UGYANABBA A RÁCSBA kerülnek, mint a többi oszlop lista-sorai — a
   * cím-oszlop részletei ELÖL. MIÉRT KÜLÖN PROP a `cardExtra` mellett: két külön `dl` két külön
   * címke-kolumnát mér, tehát az értékek a két blokkban MÁS x-pozíción kezdődnek (a hosszabb címke
   * beljebb tolja a sajátját) — a kártya „csúszik". Egyetlen rácsba fűzve minden érték a LEGHOSSZABB
   * címkéhez igazodik, a címkék pedig balra zártak. Aki nem label:érték tartalmat ad (pl. diff-panel,
   * forenzikus meta), az továbbra is a `cardExtra`-t használja — az önálló blokként, a lista ELŐTT jelenik meg.
   */
  cardExtraItems?: ((row: T) => CardDetailItem[]) | undefined;
  /**
   * EXPORT (XLSX/CSV/JSON): az oszlop NYERS szöveg-/szám-értéke egy sorból. Az oszlop **CSAK ezzel** kerül
   * az exportba — ez tudatos opt-in, nem hiányosság:
   *
   *   • a `cell` JSX-et ad (avatar, badge, legördülő), amiből szöveget kinyerni törékeny (a legacy pontosan
   *     ezt tette: a kirenderelt DOM-ot kaparta, és a badge-feliratok, a „+2" chip meg a tooltipek is
   *     bekerültek az exportba),
   *   • a művelet-oszlopnak és a dísz-oszlopoknak nincs exportálandó tartalma → nem kell `no-export` jelölés,
   *   • egy ÚJ oszlop nem szivárog be az exportba félig-kész szöveggel: aktívan hozzá kell adni.
   *
   * A visszatérés `null`/`undefined` → üres cella (nem a „null" szó).
   */
  exportValue?: ((row: T) => string | number | boolean | null | undefined) | undefined;
  /**
   * Az export-CELLA TÍPUSA (default: `text`). MIÉRT KELL: a `exportValue` string is lehet (a felületen
   * formázott alak), Excelben viszont a szöveg-cellát nem lehet **összegezni, rendezni, szűrni** — egy
   * pénzügyi vagy dátumos export így félkarú. A típus megadásával a `.xlsx` VALÓDI szám/dátum cellát kap
   * (formátummal), a CSV/JSON pedig a KANONIKUS alakot (nyers szám, ISO-dátum), ami gépi feldolgozásra jó.
   *
   * A típushoz az `exportValue`-nak a NYERS értéket kell adnia (Decimal-string vagy szám · ISO-dátum-string),
   * nem a felületi formázást — a formázás a cél-formátum dolga.
   */
  exportType?: 'text' | 'number' | 'money' | 'date' | 'datetime' | undefined;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  getRowId: (row: T) => string;
  /** Szerver-rendezés állapota + kapcsolója. */
  sort?: string;
  order?: SortOrder;
  onSort?: (col: string) => void;
  /** Kezdeti betöltés → skeleton; hiba → errorState; üres → emptyState. */
  isLoading?: boolean;
  isError?: boolean;
  emptyState?: ReactNode;
  errorState?: ReactNode;
  /** Végtelen görgetés. */
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
  /** Jobbra igazított sor-akció-slot (ikon-gombok). `undefined` → nincs akció-oszlop (jog nélküli nézet). */
  rowActions?: ((row: T) => ReactNode) | undefined;
  /** Az akció-oszlop képernyőolvasó-felirata (a fejléc vizuálisan üres). */
  rowActionsLabel?: string | undefined;
  /**
   * A MOBIL-kártyán NE legyen ⋯ menü. Akkor kell, ha a sor egyetlen akciója a REKORD MEGNYITÁSA: a kártya
   * koppintása már megnyitja, tehát a menü ugyanazt kínálja egy plusz lépéssel (user-döntés:
   * „nem kell ⋯ gomb, egyszerűen lehessen megnyitni"). A DESKTOP akció-oszlop ettől FÜGGETLEN — ott a
   * gomb marad, mert a sor-koppintás deszktopon kevésbé felfedezhető.
   */
  hideCardActions?: boolean;
  /** Az akció-oszlop FIX szélessége (grid-track). Fixnek KELL lennie (nem tartalom-méretűnek), különben a
   *  statikus fejléc és a görgetett sorok külön rácsa eltérően oldódna fel → oszlop-elcsúszás. Alap: `7rem`. */
  actionsColWidth?: string | undefined;
  onRowClick?: (row: T) => void;
  /**
   * SOR-SZINTŰ extra osztályok (pl. „olvasatlan" jelölés a partner-portál jegy-listáján). A visszaadott
   * osztályok a sor ALAP-osztályai UTÁN kerülnek, tehát felülírhatják a hátteret. `undefined` → nincs extra.
   *
   * MIÉRT PROP ÉS NEM BELSŐ SZABÁLY: az „olvasatlan" fogalom modul-specifikus (a naplónak vagy a
   * számla-listának nincs ilyen állapota), a MEGJELENÍTÉS viszont a táblát illeti — a hívó adja a jelentést.
   */
  rowClassName?: ((row: T) => string | undefined) | undefined;
  /**
   * SOR-KIJELÖLÉS (opt-in) — tömeges műveletekhez (pl. számla-kiállítás több soron). Megadva egy vezető
   * jelölő-oszlop jelenik meg, a fejlécben a „mind" kapcsolóval (a BETÖLTÖTT sorokra hat, nem a teljes
   * találatra — a végtelen görgetésnél ez az egyetlen becsületes szemantika).
   *
   * CSAK a DESKTOP-táblán jelenik meg: a mobil-kártya EGYSÉGES alakja (CC4) tudatosan zárt, és a tömeges
   * művelet ops-feladat, amit asztali gépen végeznek. A kártyán így nincs jelölő (nem is fér el értelmesen).
   */
  selection?:
    | {
        /** A kijelölt sor-id-k. */
        selectedIds: ReadonlySet<string>;
        /** Egy sor ki/bejelölése. */
        onToggle: (id: string) => void;
        /**
         * A KIJELÖLHETŐ, betöltött sorok mind-ki/bejelölése (a hívó dönti el, mit tesz a meglévő kijelöléssel).
         * Az `ids` már SZŰRT: csak azok a sorok, amelyeken az `isSelectable` igaz.
         */
        onToggleAll: (ids: string[], next: boolean) => void;
        /**
         * Melyik sor jelölhető ki egyáltalán (alap: mind). A nem jelölhető sor jelölője TILTOTT, és a
         * fejléc-jelölő SEM számol vele.
         *
         * MIÉRT KELL: a számla-listán csak PISZKOZAT állítható ki, ezért a „mind" csak azokat jelölte ki —
         * a fejléc-jelölő viszont az ÖSSZES betöltött sorhoz mérte magát, így sosem lett „bepipálva", csak
         * köztes. Radixban a köztes állapotból a kattintás BEPIPÁLTBA visz, tehát a `next` mindig `true`
         * maradt: a kijelölést NEM LEHETETT megszüntetni (user-lelet).
         */
        isSelectable?: (row: T) => boolean;
        /** A sor-jelölő aria-labelje (pl. „Sor kijelölése"). */
        rowLabel: string;
        /** A fejléc-jelölő aria-labelje (pl. „Mind kijelölése"). */
        allLabel: string;
      }
    | undefined;
  /** Hány skeleton-sor a kezdeti betöltésnél. */
  skeletonRows?: number;
  className?: string;
}

function SortIcon({ active, order }: { active: boolean; order: SortOrder }): JSX.Element {
  if (!active) return <ArrowsUpDownIcon className="h-3 w-3 shrink-0 opacity-40" />;
  return order === 'asc' ? (
    <ArrowUpIcon className="h-3.5 w-3.5 shrink-0" />
  ) : (
    <ArrowDownIcon className="h-3.5 w-3.5 shrink-0" />
  );
}

/** A kártya oszlop-csoportjai (mobil, <lg). A desktop-tábla oszlop-sorrendjét NEM érinti.
 *  Az ÖSSZECSUKOTT kártya `title` + `status`; a `details` a „Részletek" gomb alatt, kibontva jelenik meg. */
interface CardBuckets<T> {
  title: DataTableColumn<T>[];
  status: DataTableColumn<T>[];
  /** MINDEN nem-title, nem-status, nem-hidden oszlop (a desktop-adatok) — a „Részletek" alatt, label:érték listaként. */
  details: DataTableColumn<T>[];
  /** A `cardExtraItems`-t adó oszlopok — a párjaik a KÖZÖS rács ELEJÉRE kerülnek (egy címke-kolumna → az
   *  értékek egy vonalban kezdődnek). */
  extraItems: DataTableColumn<T>[];
  /** A `cardExtra`-t adó oszlopok — a tartalmuk a „Részletek" blokk ELEJÉRE kerül (a cím-oszlop részletei
   *  jönnek előbb, utána a többi oszlop label:érték listája), teljes szélességben.
   *  Slot-független: egy `title` oszlop is adhat extrát (épp ez a tipikus eset — lásd `cardCell`). */
  extras: DataTableColumn<T>[];
}

/** Egy oszlop EXPLICIT slotja: a `card` mező, vagy a `hideOnCard: true` → `'hidden'` (visszafelé-kompat.). */
function explicitSlot<T>(col: DataTableColumn<T>): CardSlot | undefined {
  if (col.card) return col.card;
  if (col.hideOnCard) return 'hidden';
  return undefined;
}

/** A kártya-slotok feloldása (best practice defaultokkal): ha nincs explicit `title` egy oszlopon sem, az ELSŐ
 *  slot-nélküli oszlop lesz a title; ha nincs explicit `status`, az `id==='status'` oszlop; a `hidden`/`hideOnCard`
 *  oszlopok kimaradnak; MINDEN MÁS (slot nélküli + subtitle/meta/trailing) a `details`-be kerül (→ „Részletek"). */
function resolveCardBuckets<T>(columns: DataTableColumn<T>[]): CardBuckets<T> {
  const hasExplicitTitle = columns.some((c) => explicitSlot(c) === 'title');
  const hasExplicitStatus = columns.some((c) => explicitSlot(c) === 'status');
  let defaultTitleTaken = false;

  const buckets: CardBuckets<T> = {
    title: [],
    status: [],
    details: [],
    extraItems: [],
    extras: [],
  };
  for (const col of columns) {
    let slot = explicitSlot(col);
    if (!slot) {
      // Default title: az első slot-nélküli oszlop (ha sehol nincs explicit title).
      if (!hasExplicitTitle && !defaultTitleTaken) {
        slot = 'title';
        defaultTitleTaken = true;
      } else if (!hasExplicitStatus && col.id === 'status') {
        slot = 'status';
      }
      // Egyébként marad `undefined` → a `details` blokkba kerül (kibontható).
    }
    if (slot === 'hidden') continue; // sehol nem jelenik meg — extrát sem ad
    // Az extra a SLOTTÓL FÜGGETLEN: a tipikus eset épp az, hogy a `title` oszlop adja (a desktop-kibontója
    // tartalmát), miközben a fejlécbe csak a címet rendereli a `cardCell`-lel.
    if (col.cardExtraItems) buckets.extraItems.push(col);
    if (col.cardExtra) buckets.extras.push(col);
    if (slot === 'title') buckets.title.push(col);
    else if (slot === 'status') buckets.status.push(col);
    else buckets.details.push(col); // subtitle/meta/trailing/slot-nélküli → „Részletek"
  }
  return buckets;
}

/** Egyetlen mobil (<lg) lista-kártya. ÖSSZECSUKVA: `title` (bal) + `status` badge + ⋯ menü — EGYSÉGES méret.
 *  A `details` oszlopok (a többi desktop-adat) a „Részletek" gomb alatt, helyben kibonthatók (label:érték).
 *  Saját (soronkénti) kibontás-állapot — ezért külön komponens (nem a `rows.map` inline-ja). */
function MobileRowCard<T>({
  row,
  buckets,
  rowActions,
  rowActionsLabel,
  detailsLabel,
  onOpen,
}: {
  row: T;
  buckets: CardBuckets<T>;
  rowActions?: ((row: T) => ReactNode) | undefined;
  rowActionsLabel?: string | undefined;
  detailsLabel: string;
  /** A rekord megnyitása (a desktop-sor `onRowClick`-je). `undefined` → a cím nem interaktív. */
  onOpen?: (() => void) | undefined;
}): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const { title, status, details, extraItems, extras } = buckets;
  // A kibontó akkor is kell, ha nincs `details` oszlop, de VAN `cardExtra` (pl. az eseménynapló: minden
  // adat-oszlopa a fejlécben/statusban ül, a forenzikus meta viszont az extrában).
  const hasDetails = details.length > 0 || extras.length > 0 || extraItems.length > 0;
  /** A kártyán a `cardCell` NYER a `cell` fölött (ha van) — a desktop-út érintése nélkül. */
  const render = (col: DataTableColumn<T>): ReactNode => (col.cardCell ?? col.cell)(row);

  // A kibontott „Részletek" EGYETLEN label:érték rácsa: elöl a cím-oszlop részletei (`cardExtraItems`),
  // utána a többi oszlop sorai — így egy címke-kolumna van, és minden érték egy vonalban kezdődik.
  const cardItems: CardDetailItem[] = [
    ...extraItems.flatMap((col) => col.cardExtraItems?.(row) ?? []),
    ...details.map((col) => ({
      key: col.id,
      label: col.cardLabel ?? col.header,
      // AZ OSZLOP SAJÁT CELLA-OSZTÁLYA A KÁRTYÁN IS ÉRVÉNYES. Deszktopon a `col.className` a cella
      // BURKOLÓJÁRA kerül (tipikusan `text-muted-foreground` vagy `tabular-nums`), és a cella JSX-e erre
      // épít: a halvány alapon emeli ki a lényeget (`text-foreground font-medium`). A kártyán ez a burkoló
      // hiányzott, tehát ugyanaz a cella VILÁGOSABB alapszínt kapott (a `dd` `text-foreground`-ját), és a
      // kiemelés elveszett — a nettó/áfa/bruttó blokk mérve más színben állt, mint deszktopon (user-lelet).
      value:
        col.className === undefined ? (
          render(col)
        ) : (
          <div className={col.className}>{render(col)}</div>
        ),
    })),
  ];

  return (
    <div
      role="row"
      // A KÁRTYA EGÉSZE megnyitja a rekordot (ha van `onRowClick`) — „block link" minta, ahogy a mobil
      // lista-felületek teszik. A BELSŐ vezérlők (`Részletek` kibontó, ⋯ menü) megállítják a buborékolást,
      // tehát azok a saját műveletüket végzik.
      //
      // ELŐZMÉNY, hogy ne essen vissza: ez SZÁNDÉKOSAN nem volt kattintható (a félre-koppintás
      // kockázata miatt), és a rekordra a ⋯ menü vitt. Csak arra a listára igaz, amelyiknek VAN ⋯ menüje —
      // a partner jegy-listán nincs, ott mobilon egyáltalán nem volt út a jegyre (user-lelet).
      // A félre-koppintást a `stopPropagation` kezeli, nem a navigáció elhagyása.
      //
      // A11Y: a kattintható DIV nem fókuszálható, ezért a KLAVIATÚRA-út a fejléc CÍM-GOMBJA (lásd lent) —
      // egy `button`-be nem lehet további `button`-t tenni, tehát a kártyát nem lehet gombbá alakítani.
      //
      // Mobil-kártya: TÖMÖR `bg-card` (mint a desktop-lista felülete), a border a közös halvány üveg-szél.
      {...(onOpen ? { onClick: onOpen } : {})}
      className={cn(
        'bg-card rounded-xl border border-[color:var(--table-hairline)] p-3',
        onOpen && 'cursor-pointer',
      )}
    >
      {/* A kártya-ág JELZI a cella-tartalomnak, hogy kártyán van (`InCardContext`): a cellák
          `DetailsExpander`-e így nem rendel MÁSODIK „Részletek" kapcsolót a kártyáé mellé — a párok a
          `cardExtraItems`-ből, egyetlen rácsba kerülnek. Lásd a `details-expander.tsx` context-kommentjét. */}
      <InCardContext.Provider value={true}>
        {/* Fejléc-sor: title (bal, flex-1, truncate) — jobbra a status-badge + ⋯ overflow-menü. Mindig látszik.
          A CÍM egyben a rekord KLAVIATÚRA-útja: `button` (a kattintható kártya-div nem fókuszálható). */}
        <div className="flex items-center justify-between gap-2">
          {onOpen ? (
            <button
              type="button"
              // `stopPropagation`: a kártya is kattintható, különben az `onOpen` kétszer futna.
              onClick={(e) => {
                e.stopPropagation();
                onOpen();
              }}
              className="text-foreground hover:text-brand min-w-0 flex-1 truncate text-left text-sm font-medium transition-colors"
            >
              {title.map((col) => (
                <span key={col.id} className="min-w-0">
                  {render(col)}
                </span>
              ))}
            </button>
          ) : (
            <div className="text-foreground min-w-0 flex-1 truncate text-sm font-medium">
              {title.map((col) => (
                <span key={col.id} className="min-w-0">
                  {render(col)}
                </span>
              ))}
            </div>
          )}
          <div className="flex shrink-0 items-center gap-1">
            {status.map((col) => (
              <span key={col.id}>{render(col)}</span>
            ))}
            {rowActions && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    aria-label={rowActionsLabel}
                    // `stopPropagation`: a kártya kattintása megnyitja a rekordot — a ⋯ CSAK a menüt nyitja.
                    onClick={(e) => e.stopPropagation()}
                    className="text-muted-foreground hover:text-foreground hover:bg-accent -mr-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors"
                  >
                    <EllipsisHorizontalIcon className="h-5 w-5" />
                  </button>
                </DropdownMenuTrigger>
                {/* A menü-tartalom a meglévő sor-akció-slotot rendeli, de `menu`-kontextusban: a RowActions/
                  RowActionButton ikon + SZÖVEGES DropdownMenuItem-ként renderel (tooltip NÉLKÜL) — a desktop-
                  toolbar ikon+tooltip változatlan. `align="end"` → jobbra igazított popover. */}
                <DropdownMenuContent align="end">
                  <RowActionsVariantContext.Provider value="menu">
                    {rowActions(row)}
                  </RowActionsVariantContext.Provider>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        {/* „Részletek" toggle + a kibontott desktop-oszlopok (label:érték). Csak ha van kibontható tartalom. */}
        {hasDetails && (
          <>
            <button
              type="button"
              aria-expanded={expanded}
              // `stopPropagation`: a kártya kattintása megnyitja a rekordot — a kibontó CSAK kibont.
              onClick={(e) => {
                e.stopPropagation();
                setExpanded((v) => !v);
              }}
              className="text-muted-foreground hover:text-foreground mt-1.5 flex w-full items-center gap-1 text-xs font-medium transition-colors"
            >
              {/* A nyíl PONTOSAN a desktop-kibontóé (20/solid, `size-3.5`): ZÁRVA jobbra, NYITVA 90°-kal
                lefelé. Korábban egy 24/outline `ChevronDown` volt `rotate-180`-nal — vagyis zárva LEFELÉ,
                nyitva FELFELÉ mutatott, és a vastagabb, nagyobb rajzolat miatt optikailag is kilógott a
                desktop-kibontó mellől. Egy app, két kibontó-jel: ugyanaz. */}
              <ChevronRightIcon
                className={cn('size-3.5 shrink-0 transition-transform', expanded && 'rotate-90')}
                aria-hidden="true"
              />
              <span>{detailsLabel}</span>
            </button>
            {/* EXTRA blokk ELSŐKÉNT: amit a desktop-cella a SAJÁT kibontójában mutat (az ELSŐ oszlop „Részletek"-je
              — pl. az eszköz azonosító-mezői, a napló forenzikus metája/diffje). Teljes szélességben, kibontó
              nélkül, mert a fenti gomb már a kapcsoló.
              MIÉRT ELŐBB, MINT AZ OSZLOP-LISTA (user-döntés 2026-08-24): a kártya CÍME az első oszlopból jön,
              tehát a hozzá tartozó részletek olvasása is ott folytatódik — a többi oszlop label:érték listája
              másodlagos. Korábban fordítva volt, és a kinyitott kártya alján kötött ki az, ami a legszorosabban
              a címhez tartozik. Ez zárja a „két Részletek egymás mellett" leletet is (lásd a `cardExtra` propot). */}
            {expanded &&
              extras.map((col, i) => (
                <div
                  key={`extra-${col.id}`}
                  className={cn(
                    'text-sm',
                    // UGYANAZ A MÉRET-ZÁR, mint a label:érték rácson: a blokk 12 px-es, tehát a benne álló
                    // `<Text>` 13 px-es alap-variánsa sem lehet nagyobb a körülötte lévő szövegnél.
                    CARD_DETAIL_SCALE,
                    // A felső hairline-t az ELSŐ blokk hozza (itt az első extra); a többi csak térközt kap.
                    i === 0 ? 'mt-2 border-t border-[color:var(--table-hairline)] pt-2' : 'mt-2',
                  )}
                >
                  {col.cardExtra?.(row)}
                </div>
              ))}
            {/* EGYETLEN RÁCS: a cím-oszlop `cardExtraItems` párjai ELÖL, utána a többi oszlop label:érték sorai.
              MIÉRT egy rácsban: két külön `dl` két külön címke-kolumnát mér, tehát az értékek a két blokkban
              MÁS x-pozíción kezdődnek (a hosszabb címke — pl. „Nyilvántartási szám" — beljebb tolja a sajátját),
              és a kártya vizuálisan „csúszik". Egy rácsban minden érték a LEGHOSSZABB címkéhez igazodik. */}
            {expanded && cardItems.length > 0 && (
              <CardDetailList
                className={cn(
                  'mt-2',
                  // Ha nincs szöveges extra-blokk felette, a felső hairline ITT kell.
                  extras.length === 0 && 'border-t border-[color:var(--table-hairline)] pt-2',
                )}
                items={cardItems}
              />
            )}
          </>
        )}
      </InCardContext.Provider>
    </div>
  );
}

export function DataTable<T>({
  columns,
  rows,
  getRowId,
  sort,
  order = 'asc',
  onSort,
  isLoading,
  isError,
  emptyState,
  errorState,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  rowActions,
  rowActionsLabel,
  hideCardActions,
  actionsColWidth = '7rem',
  onRowClick,
  rowClassName,
  selection,
  skeletonRows = 8,
  className,
}: DataTableProps<T>): JSX.Element {
  const { t } = useTranslation();
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // A FEJLÉC a görgetőn KÍVÜL ül, a sorok a görgetőn BELÜL — ha a görgetőnek látható sávja van (klasszikus
  // görgetősáv: egér csatlakoztatva, Windows, vagy `scrollbar-gutter`-t nem ismerő böngésző), a sorok a sáv
  // szélességével KESKENYEBBEK a fejlécnél, és a közös fr-sablon oszlopai elcsúsznak (a 3. oszloptól már
  // szemmel látható: „az Időszak fejléce beljebb kezdődik, mint a cellák" — user-lelet). A sáv
  // szélességét MÉRJÜK (offsetWidth − clientWidth), és a fejléc ugyanannyi jobb-belső margót kap — így a két
  // rács tartalom-doboza bájtra egyforma, böngészőtől és görgetősáv-módtól függetlenül. Overlay-sávnál 0 → no-op.
  const [scrollbarGutter, setScrollbarGutter] = useState(0);
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const measure = (): void => setScrollbarGutter(el.offsetWidth - el.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [rows.length, isLoading, isError]);

  // A kártya-slotok (title/status/details) az oszlop-configból következnek — soronként azonosak, ezért EGYSZER
  // oldjuk fel (nem a rows.map-ban). A desktop-tábla oszlop-sorrendjét ez NEM érinti.
  const cardBuckets = resolveCardBuckets(columns);

  // A callback + isFetchingNextPage stabil ref-ben — így az observer NEM épül újra minden renderen
  // (az inline `onLoadMore` új referenciája sem triggerel). A ref-et renderben frissítjük (mindig friss).
  const latest = useRef({ onLoadMore, isFetchingNextPage });
  latest.current = { onLoadMore, isFetchingNextPage };

  // Végtelen görgetés: a sentinel a kártya BELSŐ görgetőjén belül van, ezért az observer gyökere a görgető div
  // (nem a viewport). Az observer csak akkor (újra)épül, ha a sentinel megjelenik (hasNextPage) vagy a következő
  // lap betöltése lezárult (isFetchingNextPage). A friss callbacket a ref adja (closure-frissesség).
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasNextPage) return;
    if (typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting && !latest.current.isFetchingNextPage)
          latest.current.onLoadMore?.();
      },
      { root: scrollRef.current },
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, [hasNextPage, isFetchingNextPage]);

  // A fejléc és a sorok EGYETLEN, közös grid-sablonon osztoznak → tökéletes oszlop-igazítás úgy, hogy a fejléc a
  // függőleges görgetőn KÍVÜL van (statikus), a sorok pedig egy külön görgetőben → a görgetősáv az ELSŐ SORTÓL
  // indul, nem fut a fejléc mellett. FONTOS: minden track TARTALOM-FÜGGETLEN legyen (fr vagy fix px) — `max-content`/
  // `auto` a két külön rácsban (fejléc vs. sorok) eltérően oldódna fel, mert más tartalmat mér → elcsúszó oszlopok.
  // Alap oszlop `minmax(0,1fr)`: rugalmas + tartalom-vágó → nincs vízszintes kilógás. Az akció-oszlop FIX (px/rem).
  const gridTemplate = [
    // Vezető jelölő-oszlop (opt-in): FIX szélesség — tartalom-független track, mint az akció-oszlop.
    ...(selection ? ['2.5rem'] : []),
    ...columns.map((col) => col.width ?? 'minmax(0,1fr)'),
    ...(rowActions ? [actionsColWidth] : []),
  ].join(' ');

  // „Mind" állapot a KIJELÖLHETŐ, betöltött sorokra: mind kijelölve → a fejléc-jelölő bepipálva; részleges →
  // köztes. A mérce a JELÖLHETŐ halmaz, nem az összes sor (lásd az `isSelectable` jegyzetét).
  const selectableIds = selection
    ? rows.filter((r) => selection.isSelectable?.(r) ?? true).map((r) => getRowId(r))
    : [];
  const selectedCount = selection
    ? selectableIds.filter((id) => selection.selectedIds.has(id)).length
    : 0;
  const allSelected =
    selection !== undefined && selectableIds.length > 0 && selectedCount === selectableIds.length;
  const someSelected = selectedCount > 0 && !allSelected;

  if (isLoading) {
    return (
      <div
        className={cn(
          'card-glass-lg flex min-h-0 flex-1 flex-col gap-2 overflow-hidden p-4 lg:rounded-xl',
          className,
        )}
        aria-busy="true"
      >
        {Array.from({ length: skeletonRows }).map((_, i) => (
          <Skeleton key={i} className="h-6 w-full" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className={cn('flex min-h-0 flex-1 items-center justify-center', className)}>
        {errorState ?? <EmptyState title="—" />}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      // A `min-h-[15rem]`: az ÜRES állapot ne omlassza össze a befoglaló kártyát. Az ADMIN lista-oldalain a
      // tábla egy flex-stretch kolumnában áll, ezért ott magas volt magától; a PARTNER-PORTÁL lapjai viszont
      // `space-y` szekciókból állnak, és ott az üres-állapot kártyája „nagyon alacsony" lett (user-mérés).
      // A minimum mindkét felületen ugyanezt a formát adja.
      <div
        // A `min-h-0`-t itt SZÁNDÉKOSAN elhagyjuk: az üres ágban nincs mit zsugorítani, és a két utility
        // (`min-h-0` vs. `min-h-[15rem]`) ugyanazt a property-t írja — a sorrendjük CSS-en dőlne el.
        className={cn('flex min-h-[15rem] flex-1 items-center justify-center', className)}
      >
        {emptyState ?? <EmptyState title="—" />}
      </div>
    );
  }

  return (
    // RÖGZÍTETT, lekerekített kártya (overflow-hidden → a sarkok VÉGIG kerekek). role="table" a11y-hoz.
    // A fejléc a görgetőn KÍVÜL, statikusan ül; alatta a sorok saját görgetőben → a görgetősáv az első sortól indul.
    <div
      role="table"
      className={cn(
        'card-glass-lg flex min-h-0 flex-1 flex-col overflow-hidden lg:rounded-xl',
        className,
      )}
    >
      {/* Fejléc (lg+) — statikus, a függőleges görgetőn kívül; a sorokkal közös grid-sablonon. A test görgetőjének
          MÉRT sáv-szélessége jobb-belső margóként kerül rá (lásd `scrollbarGutter`) → az oszlopok akkor is
          igazodnak, ha a rendszeren klasszikus (nem-overlay) görgetősáv van. Minden oszlop BALRA igazított —
          a fejléc és a cella ugyanazon a bal élen kezdődik (rendszer-alapértelmezés, user-döntés 2026-09-15). */}
      <div
        role="row"
        className={cn(
          'text-muted-foreground bg-card z-10 hidden shrink-0 items-center overflow-hidden text-left lg:grid',
          HAIRLINE_BOTTOM,
        )}
        style={{ gridTemplateColumns: gridTemplate, paddingRight: scrollbarGutter }}
      >
        {selection && (
          <div role="columnheader" className="flex items-center justify-center px-2 py-2">
            <Checkbox
              checked={allSelected ? true : someSelected ? 'indeterminate' : false}
              onCheckedChange={(c) => selection.onToggleAll(selectableIds, c === true)}
              aria-label={selection.allLabel}
            />
          </div>
        )}
        {columns.map((col) => {
          const key = col.sortKey ?? col.id;
          const active = sort === key;
          return (
            <Text
              key={col.id}
              as="div"
              variant="overline"
              role="columnheader"
              aria-sort={active ? (order === 'asc' ? 'ascending' : 'descending') : undefined}
              // `truncate` (overflow-hidden + ellipsis + nowrap): a fejléc-cellák SOHA ne lógjanak egymásba,
              // ha az ablakot összehúzzák — a nem-rendezhető fejléc-szöveg itt vágódik; a rendezhetőt a belső
              // gomb/label truncate-je vágja (a rács-track logika [minmax(0,1fr)] változatlan).
              className={cn('min-w-0 truncate px-3.5 py-2', col.headerClassName)}
            >
              {col.sortable && onSort ? (
                <button
                  type="button"
                  onClick={() => onSort(key)}
                  // A Tailwind preflight a `button`-ra `text-transform: none`-t tesz → felülírja az `overline`
                  // örökölt `uppercase`-ét; ezért itt explicit visszaadjuk (a fejléc egységesen NAGYBETŰS).
                  // `min-w-0` → a flex-gomb szűkülhet a cella alá; a label-span truncate-je így tud vágni.
                  className="hover:text-foreground flex min-w-0 items-center gap-1 uppercase"
                  aria-label={typeof col.header === 'string' ? col.header : undefined}
                >
                  <span className="min-w-0 truncate">{col.header}</span>
                  <SortIcon active={active} order={order} />
                </button>
              ) : (
                col.header
              )}
            </Text>
          );
        })}
        {rowActions && (
          <div role="columnheader" className="px-3.5 py-2">
            {rowActionsLabel && <span className="sr-only">{rowActionsLabel}</span>}
          </div>
        )}
      </div>

      {/* Görgetett test — CSAK ez görög (függőlegesen); vízszintesen nincs kilógás (grid-oszlopok vágnak).
          A `scrollbar-gutter: stable` a fejléccel egyező sávot foglal → nincs oszlop-elcsúszás. */}
      <div
        ref={scrollRef}
        className="text-md text-muted-foreground flex-1 overflow-y-auto overflow-x-hidden [scrollbar-gutter:stable]"
      >
        {/* Sorok (lg+) — a fejléccel közös grid-sablon. */}
        <div role="rowgroup" className="hidden lg:block">
          {rows.map((row) => (
            <div
              key={getRowId(row)}
              role="row"
              // STABIL SOR-AZONOSÍTÓ a DOM-ban: az e2e-tesztek ebből tudják eldönteni, hogy a lapozás
              // ÁTFEDÉS-MENTES lapot adott-e (a sor-szöveg nem alkalmas rá: a napló-sorok szövege ismétlődhet).
              // Korábban létezett, egy refaktorban kiesett — a lapozás-teszt így csendben elvesztette az
              // azonosítóját.
              data-row-id={getRowId(row)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              style={{ gridTemplateColumns: gridTemplate }}
              className={cn(
                // Sor-elválasztó: halvány kártya-szegély-szín (`--table-hairline`), pár px-szel BELJEBB húzva
                // (inset-x-3 pszeudó-vonal), hogy ne érjen a kártya széléig — az utolsó sor alatt nincs.
                'hover:bg-accent/60 grid items-center transition-colors',
                HAIRLINE_ROW,
                onRowClick && 'cursor-pointer',
                rowClassName?.(row),
              )}
            >
              {selection && (
                <div
                  role="cell"
                  className="flex items-center justify-center px-2 py-2.5"
                  // A jelölő NEM indítja a sor-navigációt (a kijelölés és a megnyitás két külön szándék).
                  onClick={(e) => e.stopPropagation()}
                >
                  <Checkbox
                    checked={selection.selectedIds.has(getRowId(row))}
                    onCheckedChange={() => selection.onToggle(getRowId(row))}
                    disabled={!(selection.isSelectable?.(row) ?? true)}
                    aria-label={selection.rowLabel}
                  />
                </div>
              )}
              {columns.map((col) => (
                <div
                  key={col.id}
                  role="cell"
                  className={cn(
                    'min-w-0 px-3.5 py-2.5',
                    col.multiline ? 'overflow-hidden' : 'truncate',
                    col.className,
                  )}
                >
                  {col.cell(row)}
                </div>
              ))}
              {rowActions && (
                <div
                  role="cell"
                  className="flex justify-end px-3.5 py-2.5"
                  onClick={(e) => e.stopPropagation()}
                >
                  {rowActions(row)}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Kártyás fallback (< lg): EGYSÉGES méretű kártya. ÖSSZECSUKVA csak a `title` (bal) + `status` badge (jobb)
            + ⋯ menü látszik → minden kártya egyforma, a lista jól pásztázható. A többi desktop-oszlop a „Részletek"
            gomb alatt, HELYBEN kibontva (label:érték) jelenik meg — nem visz el a listából. */}
        <div role="rowgroup" className="space-y-2 lg:hidden">
          {rows.map((row) => (
            <MobileRowCard
              key={getRowId(row)}
              row={row}
              buckets={cardBuckets}
              {...(hideCardActions ? {} : { rowActions })}
              rowActionsLabel={rowActionsLabel}
              detailsLabel={t('common.details')}
              onOpen={onRowClick ? () => onRowClick(row) : undefined}
            />
          ))}
        </div>

        {/* Végtelen-görgetés sentinel (a görgetőn belül) + „továbbtöltés" jelző. */}
        {hasNextPage && (
          <div ref={sentinelRef} className="py-4 text-center" aria-hidden="true">
            {isFetchingNextPage && <span className="text-muted-foreground text-xs">…</span>}
          </div>
        )}
      </div>
    </div>
  );
}
